import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// P17-UAT-15 REV-D1 · QA Weight Register — read-only frozen evidence view.

const inp = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const card = "border border-[var(--border)] rounded-md bg-[var(--bg-1)] p-4";
const fmt = (v, d = 3) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d));
const STATUS_CLS = {
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  CONFIRMED: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  AUTHORISED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};
const TOL_CLS = { WITHIN: "text-emerald-300", OUT: "text-amber-300", UNKNOWN: "text-zinc-400" };

export default function QAWeightRegister() {
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (product) params.set("product_code", product);
      if (status) params.set("status", status);
      const { data } = await api.get(`/qa/running-weights?${params.toString()}`);
      setRows(data.records || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load register"); }
  }, [product, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="qa-weight-register-page">
      <div>
        <h1 className="text-2xl font-semibold text-white">QA Weight Register</h1>
        <p className="text-sm text-zinc-500">Read-only · standard vs actual · tolerance disposition · authorisation evidence</p>
      </div>
      <div className={card}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Product Code</label>
            <input className={inp} data-testid="qa-reg-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="filter by product" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Status</label>
            <select className={inp} data-testid="qa-reg-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option><option value="DRAFT">Draft</option>
              <option value="CONFIRMED">Confirmed</option><option value="AUTHORISED">Authorised</option>
            </select>
          </div>
          <button data-testid="qa-reg-refresh" onClick={load} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-300 text-sm hover:bg-white/5">Refresh</button>
        </div>
      </div>
      <div className={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
              <th className="px-2 py-2 text-left">Product</th><th className="px-2 py-2 text-left">Prod. Date</th>
              <th className="px-2 py-2 text-right">Std</th><th className="px-2 py-2 text-right">Actual</th>
              <th className="px-2 py-2 text-right">Diff</th><th className="px-2 py-2 text-right">Var %</th>
              <th className="px-2 py-2 text-right">Tol %</th><th className="px-2 py-2 text-center">Result</th>
              <th className="px-2 py-2 text-center">Obs (valid/excl)</th><th className="px-2 py-2 text-left">QA Manager</th>
              <th className="px-2 py-2 text-center">Status</th>
            </tr></thead>
            <tbody data-testid="qa-reg-rows">
              {rows.map((r) => {
                const valid = (r.observations || []).filter((o) => o.valid).length;
                const excl = (r.observations || []).length - valid;
                return (
                  <tr key={r.id} onClick={() => setDetail(r)} className="border-b border-[var(--border)]/40 cursor-pointer hover:bg-white/5" data-testid={`qa-reg-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono text-zinc-200">{r.product_code}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-400">{r.production_date}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(r.standard_gpm, 3)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(r.actual_gpm, 3)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(r.difference_gpm, 3)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(r.variance_pct, 2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmt(r.tolerance_pct, 2)}</td>
                    <td className={`px-2 py-1.5 text-center font-mono ${TOL_CLS[r.tolerance_result] || "text-zinc-500"}`}>{r.tolerance_result || "—"}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-zinc-400">{valid}/{excl}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-300">{r.authorized_by || "—"}</td>
                    <td className="px-2 py-1.5 text-center"><span className={`text-[9px] font-bold px-1 py-0.5 rounded-sm border uppercase ${STATUS_CLS[r.status] || ""}`}>{r.status}</span></td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={11} className="px-2 py-4 text-center text-zinc-600">No records</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {detail && (
        <div className={card} data-testid="qa-reg-detail">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-white">{detail.product_code} · {detail.production_date} · Observations</div>
            <button onClick={() => setDetail(null)} className="text-zinc-500 hover:text-white text-xs">Close</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(detail.observations || []).map((o) => (
              <div key={o.seq} className={`px-2 py-1 rounded-sm border text-xs font-mono ${o.valid ? "border-emerald-500/40 text-emerald-200" : "border-red-500/40 text-red-300 line-through"}`}
                title={o.valid ? "" : `Excluded: ${o.exclusion_reason}`}>R{o.seq}: {fmt(o.value_gpm, 2)}</div>
            ))}
          </div>
          {detail.manager_remark && <div className="mt-2 text-xs"><span className="text-zinc-500 uppercase text-[10px]">Manager Remark: </span><span className="text-amber-200">{detail.manager_remark}</span></div>}
        </div>
      )}
    </div>
  );
}
